import { Dispatch } from 'redux';
import { PasswordInputWithValidation } from 'acscp/components/common/validation/passwordInputWithValidation';
import { AppState } from 'acscp/store';
import React from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { PasswordCreateRequest, PasswordResetRequest, RequestClass } from 'acscp/api/requests';
import { AddNotification, FetchRequest, FetchResetStatus } from 'acscp/actions';
import { RequestStateProps } from 'acscp/components/common/helpers/requestStateProps';
import { Form } from 'acscp/components/common/form';
import { ButtonWithLoadingIndicator } from 'acscp/components/common/buttonWithLoadingIndicator';
import { FormattedMessage } from 'react-intl';
import { withAnonymousUserLayout } from 'acscp/components/layout/anonymousUserLayout';
import { defineMessages, InjectedIntlProps, injectIntl } from 'react-intl';
import { FormValidator, ValidationRule } from 'acscp/components/common/helpers/formValidator';
import { StringValidationChecks } from 'acscp/components/common/helpers/validationChecks';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { AcceptTAndCs } from 'acscp/components/common/acceptTAndCs';
import { PasswordStrengthAssessment } from 'acscp/components/common/passwordStrengthAssessment';
import { RequestErrorMessage } from 'acscp/components/common/requestErrorMessage';
import { NotificationType } from 'acscp/store/notificationState';

const messages = defineMessages({
    passwordError: {
        id: 'web.components.pages.passwordReset.passwordRequired',
        defaultMessage: 'Password is required.'
    },
    passwordRetypeError: {
        id: 'web.components.pages.passwordReset.passwordMismatch',
        defaultMessage: 'Passwords should match.'
    },
    creationHeader: {
        id: 'web.components.pages.passwordReset.formHeaderCreation',
        defaultMessage: 'CREATE A PASSWORD'
    },
    resetHeader: {
        id: 'web.components.pages.passwordReset.formHeaderReset',
        defaultMessage: 'PASSWORD RESET'
    },
    creationDescription: {
        id: 'web.components.pages.passwordReset.descriptionCreation',
        defaultMessage: 'Your account will be linked to your email address.'
    },
    resetDescription: {
        id: 'web.components.pages.passwordReset.descriptionReset',
        defaultMessage: 'Enter your new password and retype to confirm.'
    },
    passwordPlaceholder: {
        id: 'web.components.pages.passwordReset.passwordPlaceholder',
        defaultMessage: 'PASSWORD'
    },
    passwordRetypePlaceholder: {
        id: 'web.components.pages.passwordReset.passwordRetypePlaceholder',
        defaultMessage: 'RETYPE PASSWORD'
    },
    tokenError: {
        id: 'web.components.pages.passwordReset.tokenError',
        defaultMessage: 'Sorry, we didn\'t recognise your reset code.'
    }
});

interface PassedOwnProps {
    isCreatingPassword: boolean;
}
type UntranslatedPasswordResetOwnProps = PassedOwnProps & RouteComponentProps<any>;
type PasswordResetOwnProps = UntranslatedPasswordResetOwnProps & InjectedIntlProps;

type PasswordResetStateProps = RequestStateProps;

interface PasswordResetDispatchProps {
    passwordReset:
        (username: string, resetToken: string, password: string, loyaltyScheme: boolean) => void;
    resetPasswordRequestStatus: () => void;
    addResetTokenErrorNotification: () => void;
}

interface PasswordResetForm {
    username: string;
    resetToken: string;
    password: string;
    isLoyaltySchemeOnly: boolean;
    passwordRetype: string;
    acceptTAndCs?: boolean;
}

interface PasswordResetInternalState extends PasswordResetForm {
    passwordStrongEnough: boolean;
    showErrors: boolean;
}

type PasswordResetProps = PasswordResetOwnProps & PasswordResetStateProps & PasswordResetDispatchProps;

export class PasswordResetPresentational extends React.Component<PasswordResetProps, PasswordResetInternalState> {
    private validator: FormValidator;

    constructor(props: PasswordResetProps) {
        super(props);

        this.validator = new FormValidator(this.getValidationRules());

        let username = '';
        let resetToken = '';
        let isLoyaltySchemeOnly = false;

        if (props.location) {
            const params = new URLSearchParams(props.location.search);

            username = params.get('email') || '';
            resetToken = params.get('token') || '';
            isLoyaltySchemeOnly = params.has('isLoyaltySchemeOnly');
        }

        this.state = {
            username, resetToken, password: '',
            isLoyaltySchemeOnly,
            passwordRetype: '', showErrors: false,
            passwordStrongEnough: false,
        };
    }

    private getValidationRules(): ValidationRule[] {
        const translator = this.props.intl.formatMessage;
        return [
            {
                validatedPropertyName: 'password',
                validationCheck: StringValidationChecks.isRequired(() => this.state.password),
                errorMessage: translator(messages.passwordError)
            },
            {
                validatedPropertyName: 'passwordRetype',
                validationCheck: StringValidationChecks.stringMatch(() => this.state.passwordRetype, () => this.state.password),
                errorMessage: translator(messages.passwordRetypeError)
            }
        ];
    }

    passwordChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ password: event.target.value })

    passwordRetypeChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ passwordRetype: event.target.value })

    isStateValid = () => {
        return this.validator.isStateValid() &&
            (!this.props.isCreatingPassword || this.state.acceptTAndCs)
            && this.state.passwordStrongEnough;
    }

    passwordReset = () => {
        this.props.resetPasswordRequestStatus();

        if (!this.props.isLoading) {
            if (this.isStateValid()) {
                this.props.passwordReset(this.state.username, this.state.resetToken,
                    this.state.password, this.state.isLoyaltySchemeOnly);
            }
        }

        this.setState({ showErrors: true });
    }

    passwordStrength = (strong: boolean) => {
        this.setState({passwordStrongEnough: strong});
    }

    toggleTermsAndConditionsCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
        return this.setState({ acceptTAndCs: event.target.checked });
    }

    render(): JSX.Element {
        const { requestError, intl, isCreatingPassword, isLoading } = this.props;
        const translator = intl.formatMessage;
        const { resetToken, password, passwordRetype } = this.state;

        if (resetToken.length === 0) {
            this.props.addResetTokenErrorNotification();
            return (<Redirect to={'/login'} />);
        }

        const validationErrors = this.validator.getDisplayedErrors(this.state.showErrors, requestError);

        const formHeader = translator(isCreatingPassword
            ? messages.creationHeader
            : messages.resetHeader);

        const formDescription = translator(isCreatingPassword
            ? messages.creationDescription
            : messages.resetDescription);

        return (
            <Form
                submit={this.passwordReset}
                header={formHeader}
            >
                <span>{formDescription}</span>
                <PasswordInputWithValidation
                    name="password"
                    placeholder={translator(messages.passwordPlaceholder)}
                    value={password}
                    autoComplete="new-password"
                    onChange={this.passwordChanged}
                    validationErrors={validationErrors}
                />
                <PasswordStrengthAssessment
                    password={password}
                    passwordStrengthCallback={this.passwordStrength}
                    intl={this.props.intl}
                />
                <PasswordInputWithValidation
                    name="passwordRetype"
                    placeholder={translator(messages.passwordRetypePlaceholder)}
                    value={passwordRetype}
                    autoComplete="new-password"
                    onChange={this.passwordRetypeChanged}
                    validationErrors={validationErrors}
                />
                {this.props.isCreatingPassword &&
                    <div>
                        <AcceptTAndCs
                            toggleTAndCsFunction={this.toggleTermsAndConditionsCheckbox}
                            showErrors={this.state.showErrors}
                            borderedBox={true}
                            acceptTAndCsIsChecked={this.state.acceptTAndCs}
                        />
                    </div>
                }
                <ButtonWithLoadingIndicator isLoading={isLoading}>
                    <FormattedMessage
                        id={'web.components.pages.passwordReset.submit'}
                        defaultMessage={'SET PASSWORD AND LOG IN'}
                    />
                </ButtonWithLoadingIndicator>
                <RequestErrorMessage requestState={this.props} />
            </Form>

        );
    }

}

function mapStateToProps(
    state: AppState,
    ownProps: UntranslatedPasswordResetOwnProps,
    getRequestError: (request: RequestClass<any>) => RequestStateProps
): PasswordResetStateProps {
    return getRequestError(ownProps.isCreatingPassword ? PasswordCreateRequest : PasswordResetRequest);
}

function mapDispatchToProps(
    dispatch: Dispatch, ownProps: UntranslatedPasswordResetOwnProps
): PasswordResetDispatchProps {

    return {
        passwordReset: (username: string, resetToken: string, password: string, isLoyaltySchemeOnly: boolean) =>
            dispatch(new FetchRequest(ownProps.isCreatingPassword ?
                new PasswordCreateRequest(username, resetToken, password, isLoyaltySchemeOnly)
                : new PasswordResetRequest(username, resetToken, password))),
        resetPasswordRequestStatus: () => dispatch(new FetchResetStatus(PasswordResetRequest)),
        addResetTokenErrorNotification: () =>
            dispatch(new AddNotification('PasswordReset', messages.tokenError, NotificationType.Error))
    };
}

const PasswordResetPage =
    withAnonymousUserLayout(
        connectWithRequests(mapStateToProps, mapDispatchToProps)(
            injectIntl(
                PasswordResetPresentational)));

const CreatePassword: React.SFC<RouteComponentProps<any>> =
    props => (
        <PasswordResetPage {...props} isCreatingPassword={true} />
    );

const ResetPassword: React.SFC<RouteComponentProps<any>> =
    props => (<PasswordResetPage {...props} isCreatingPassword={false} />);

export { CreatePassword, ResetPassword };
